import { Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useState } from 'react';

import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';

import { claimApi } from '@/main/api';
import { ClaimGridItemDetailResponse, ClaimGridItemPartResponse, ClaimSearchGridResponse } from '@/main/models/response';
import { getTranslateByFullKey, getTranslateByKey, isEmpty, isKeyTranslate, isNullUndefined, isShowControlByElementId } from '@/main/services/utils';

import { PossibleTotalLossIcon } from '@/main/components/shared/icons';
import ModalContactsWithIcon from '@/main/components/shared/modalContacts/modalContactsWithIcon';
import ProcurementStatusComponent from '@/main/components/shared/status/procurementStatusComponent';
import { KeyValue } from '@/main/models/common';
import { Controls } from '@/main/services/constants/constantsControl';

import { useSearchClaimContext } from '../../context';
import ClaimDetailsSearchClaim from './claimDetailsSearchClaim/claimDetailsSearchClaim';
import CollapseSearchClaim from './collapseSearchClaim/collapseSearchClaim';
import * as S from './styles';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';


interface IRowSearchClaimProps {
  searchClaim: ClaimSearchGridResponse;
  index: number;
  refreshClaimCounts: () => void;
}

function RowSearchClaim({
  searchClaim,
  index,
  refreshClaimCounts
}: IRowSearchClaimProps) {
  const showPrices = isShowControlByElementId(Controls.IdShowPrices);
  const {
    formFilters,
    refreshRowByGuidClaim
  } = useSearchClaimContext();

  const [expanded, setExpanded] = useState(false);
  const [openModalClaimDetail, setOpenModalClaimDetail] = useState(false);
  const [dataDetailClaim, setDataDetailClaim] = useState<ClaimGridItemDetailResponse | null>(null);
  const [isLoadingDetailClaim, setIsLoadingDetailClaim] = useState<boolean>(false);

  const [dataParts, setDataParts] = useState<ClaimGridItemPartResponse[] | null>(null);
  const [isLoadingDataParts, setIsLoadingDataParts] = useState<boolean>(false);

  //#region Async functions
  async function loadGridParts(claimNumber: string) {
    setIsLoadingDataParts(true);
    claimApi
      .getgridparts(claimNumber)
      .then(response => {
        const { result } = response.data;
        if (isEmpty(result)) return
        const filteredResult = formFilters?.onlyDelayedParts ? result.filter(item => item.delayed) : result;

        setDataParts(filteredResult);
      })
      .finally(() => setIsLoadingDataParts(false));
  }

  async function loadDetailClaim(guidClaim: string) {
    setIsLoadingDetailClaim(true);

    claimApi
      .getGridItemDetails(guidClaim)
      .then(response => {
        const { data: { result } } = response;
        setDataDetailClaim(result);
      })
      .finally(() => {
        setIsLoadingDetailClaim(false);
      });
  };

  async function refreshClaim() {
    const result = await Promise.all([
      loadDetailClaim(searchClaim.guidClaim),
      loadGridParts(searchClaim.guidClaim)
    ]);

    refreshRowByGuidClaim(searchClaim.guidClaim);
  }

  function updateGridPartByGuidQuoteItem(guidQuoteItem: string, objUpdate: Partial<ClaimGridItemPartResponse>) {
    if (isNullUndefined(dataParts) || isEmpty(guidQuoteItem)) return;

    const newDataParts = dataParts.map(item => {
      if (item.guidQuoteItem === guidQuoteItem) {
        return {
          ...item,
          ...objUpdate
        }
      }
      return item;
    });

    setDataParts(newDataParts);
  };

  //#endregion

  //#region Handle funtions
  function handleClickRow(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);

    if (expanded) return;
    if (isNullUndefined(dataDetailClaim)) loadDetailClaim(searchClaim.guidClaim);
    if (isNullUndefined(dataParts)) loadGridParts(searchClaim.guidClaim);
  }
  //#endregion

  //#region Render functions
  const renderTextColumn = (text: string | number | undefined, type: 'totalLoss' | null = null) => {
    if (!text) {
      const isTotalLoss = type === 'totalLoss' && searchClaim.possibleTotalLoss;
      if (!isTotalLoss) return '-'

      return (
        <>
          {'-'}
          <PossibleTotalLossIcon possibleTotalLoss={searchClaim.possibleTotalLoss} />
        </>
      );
    };


    if (isEmpty(text)) return '-'
    const objFormatted = {
      totalLoss: <>{Number(text).toFixed(2)}% <PossibleTotalLossIcon possibleTotalLoss={searchClaim.possibleTotalLoss} /></>
    }
    const formattedText = !isNullUndefined(type) ? objFormatted[type] : text;
    const formattedTooltipText = !isNullUndefined(type) ? `${Number(text).toFixed(2)}%` : String(text);
    return <S.TextColumnLabel title={formattedTooltipText}>{formattedText}</S.TextColumnLabel>;
  }

  const renderTextDetails = (text: string | number | undefined) => {
    if (isEmpty(text)) return '-'
    return text;
  }

  const renderStatus = (status: string) => {
    if (isEmpty(status)) return '-';
    if (isKeyTranslate(status)) return getTranslateByFullKey(searchClaim.claimStatus);

    return status;
  }
  //#endregion

  //#region Hide columns
  const showColumnTotalLoss = showPrices;
  const showColumnInsurer = useKogControlElementId(Controls.IdShowInsurerColumnInClaimSearch);
  const numberColumnsHide = [showColumnTotalLoss, showColumnInsurer].reduce((acc, value) => acc + (value ? 0 : 1), 0);
  const showPricesToolTip = showPrices;
  //#endregion
  return (
    <S.ContainerRow $hasExpand={expanded}>
      <S.WrapperContainer>
        <S.Container
          $numbersColumnsHidden={numberColumnsHide}
          onClick={handleClickRow}
          id={`id-searchClaim-collapse-${index}`}
        >
          <ColumnLabel
            label={getTranslateByKey('information.claimNr')}
            content={
              <S.ColumnClaim>
                {renderTextColumn(searchClaim.claimNumber)}
                <ClaimDetailsSearchClaim
                  guidClaim={searchClaim.guidClaim}
                  openModalClaimDetailsSearchClaim={openModalClaimDetail}
                  setOpenModalClaimDetailsSearchClaim={setOpenModalClaimDetail}
                  showPrices={showPricesToolTip}
                />
                {/* <TooltipComponent
                  placement="right-start"
                  width={430}
                  maxWidth={430}
                  onOpen={handleHoverTooltip}
                  title={
                    <TooltipClaimDetails
                      showPrices={showPricesToolTip}
                      dataTooltip={dataTooltip}
                      isLoadingTooltip={isLoadingTooltip}
                    />
                  }>
                  <IconButton
                    size="medium"
                    color="primary"
                    style={{ marginLeft: 5 }}
                    id="id-images-modal-download-button">
                    <Info />
                  </IconButton>
                </TooltipComponent> */}
              </S.ColumnClaim>
            }
          />


          <ColumnLabel label={getTranslateByKey('information.creationDate')} content={renderTextColumn(searchClaim.creationDate)} />
          {showColumnTotalLoss && <ColumnLabel label={`${getTranslateByKey("information.totalLoss")}`} content={renderTextColumn(searchClaim.totalLoss, 'totalLoss')} />}
          <ColumnLabel label={getTranslateByKey('information.vehicle')} content={renderTextColumn(searchClaim.vehicle)} />
          {showColumnInsurer && <ColumnLabel label={getTranslateByKey('information.insurer')} content={renderTextColumn(searchClaim.insurer)} />}
          <ColumnLabel
            label={getTranslateByKey('information.bodyshop')}
            content={
              <S.ColumnClaim>
                {renderTextColumn(searchClaim.bodyshop)}

                <ModalContactsWithIcon
                  idScreen="id-screen-claim"
                  guidCompany={searchClaim.guidBodyshopCompany}
                  infoProcurement={{
                    claimNumber: String(searchClaim.claimNumber),
                    insurerName: searchClaim.insurer,
                    companyName: searchClaim.bodyshop,
                    orderNumber: searchClaim.claimNumber,
                    vehicleName: searchClaim.vehicle
                  }}
                />
              </S.ColumnClaim>
            }

          />
          <ProcurementStatusComponent status={{ id: searchClaim.idClaimStatus, name: renderStatus(searchClaim.claimStatus) }} />

          {expanded ?
            <ExpandLess
              color='primary'
              aria-expanded={expanded}
              aria-label={getTranslateByKey('information.showMore')}
            />
            :
            <ExpandMore
              color='primary'
              aria-expanded={expanded}
              aria-label={getTranslateByKey('information.showMore')}
            />

          }
        </S.Container>
      </S.WrapperContainer>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CollapseSearchClaim
          dataParts={dataParts}
          dataDetailClaim={dataDetailClaim}
          dataGrid={searchClaim}
          isLoadingDetailClaim={isLoadingDetailClaim}
          isLoadingDataParts={isLoadingDataParts}
          refreshClaim={refreshClaim}
          refreshClaimCounts={refreshClaimCounts}
          updateGridPartByGuidQuoteItem={updateGridPartByGuidQuoteItem}
        />
      </Collapse>
    </S.ContainerRow>
  )
}
export default RowSearchClaim